import React, {useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import {format, formatISO, isValid} from 'date-fns';
import {useEvent, useStore} from 'effector-react';
import i18next from 'i18next';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import {toast} from 'react-toastify';
import * as yup from 'yup';

import {BackTo} from 'components/common/BackTo';
import {Button, ButtonVariant} from 'components/ui-kit/Button';

import {DatePicker} from 'components/ui-kit/DatePicker';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {isAdmin$} from 'models/auth';
import {
    fetchDropoutBatchesFx,
    recallDataChanged,
    recallBatchFx,
    showRecallApproveModalChanged,
    uploadState$,
} from 'models/registries/gtinsGrid/upload';

import {BatchRecallParams, DropoutBatchResponse, RecallData, RecallRouteState} from 'types/gtins';
import {useDropoutDictionaryOptions} from 'utils/dropoutDictionary';

import {BatchRecall} from './BatchRecall';
import {
    BatchSelectionActions,
    BatchSelectionDetails,
    BatchSelectionItem,
    BatchSelectionList,
    BatchSelectionMeta,
    BatchSelectionModalContent,
    BatchSelectionNumber,
    BatchSelectionRadio,
} from './BatchRecall/styled';
import {RecallApproveModal} from './RecallApproveModal';
import {ButtonWrapper, ModalText, Root} from './RecallApproveModal/styled';
import {
    ButtonInner,
    ContentWrapper,
    DatePickerField,
    FormField,
    FormWrapper,
    HeadlineText,
    NavigationButtonsContainer,
    SubheadlineText,
} from './styled';
import {useRecallDocument} from './useRecallDocument';

const BATCH_NUMBER_REGEX = /^[A-Z0-9_-]{1,50}$/;
const PRODUCT_RECALL_REASON = 'PRODUCT_RECALL';

const formatBatchDate = (value?: string | null) => {
    if (!value) return '-';

    const date = new Date(value);
    return isValid(date) ? format(date, 'dd.MM.yyyy') : '-';
};

const schema = yup.object().shape({
    batchNumberSource: yup
        .mixed<'byCode' | 'byCodeAndBatchNumber'>()
        .oneOf(['byCode', 'byCodeAndBatchNumber'])
        .required(),
    batchNumber: yup.string().when('batchNumberSource', {
        is: 'byCodeAndBatchNumber',
        then: (s) =>
            s
                .required(i18next.t('errors.batchNumber.required'))
                .matches(BATCH_NUMBER_REGEX, i18next.t('errors.batchNumber.invalidFormat')),
        otherwise: (s) =>
            s
                .notRequired()
                .nullable()
                .matches(BATCH_NUMBER_REGEX, {
                    message: i18next.t('errors.batchNumber.invalidFormat'),
                    excludeEmptyString: true,
                }),
    }),
    reason: yup.string().required(i18next.t('components.form.required')),
    productCode: yup.string().required(i18next.t('components.form.required')),
    recallDate: yup
        .date()
        .nullable()
        .required('components.form.required'),
    documentNumber: yup.string().when('batchNumberSource', {
        is: 'byCodeAndBatchNumber',
        then: (s) => s.notRequired().nullable(),

        otherwise: (s) => s.required(i18next.t('errors.batchNumber.required')),
    }),
    comment: yup.string().nullable(),
});

export const RecallPage = () => {
    const isAdmin = useStore(isAdmin$);
    const location = useLocation<RecallRouteState>();
    const isAuditRecall = !!location.state?.batchNumberSource;

    return isAdmin || isAuditRecall ? <AdminRecallPage /> : <BatchRecall />;
};

const AdminRecallPage = () => {
    const {t} = useTranslation();
    const location = useLocation<RecallRouteState>();
    const routeState = location.state;
    const {reasons} = useDropoutDictionaryOptions();
    const {isRecallPending} = useStore(uploadState$);
    const searchBatches = useEvent(fetchDropoutBatchesFx);
    const recallBatch = useEvent(recallBatchFx);
    const isBatchSearchPending = useStore(fetchDropoutBatchesFx.pending);
    const isBatchRecallPending = useStore(recallBatchFx.pending);
    const [batches, setBatches] = useState<DropoutBatchResponse[]>([]);
    const [selectedBatch, setSelectedBatch] = useState<DropoutBatchResponse | null>(null);
    const [isBatchSelectionOpen, setIsBatchSelectionOpen] = useState(false);
    const [isBatchConfirmationOpen, setIsBatchConfirmationOpen] = useState(false);
    const {control, register, handleSubmit, formState, getValues, setValue, watch} = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            batchNumberSource: routeState?.batchNumberSource ?? 'byCode',
            reason: PRODUCT_RECALL_REASON,
            batchNumber: routeState?.batchNumber ?? '',
            productCode: routeState?.productCode ?? '',
            recallDate: null,
            documentNumber: '',
            comment: '',
        },
    });

    useEffect(() => {
        register('batchNumberSource');
    }, [register]);

    const batchNumberSource = watch('batchNumberSource');
    const productCode = watch('productCode');
    const batchNumber = watch('batchNumber');
    const isBatchMode = batchNumberSource === 'byCodeAndBatchNumber';
    const recallDocument = useRecallDocument(JSON.stringify([watch(), selectedBatch?.batchId]));
    const isPending =
        isRecallPending || isBatchSearchPending || isBatchRecallPending || recallDocument.pending;

    useEffect(() => {
        setBatches([]);
        setSelectedBatch(null);
        setIsBatchSelectionOpen(false);
        setIsBatchConfirmationOpen(false);
    }, [batchNumberSource, batchNumber, productCode]);

    const onSubmit = async (data: RecallData) => {
        if (data.batchNumberSource === 'byCode') {
            recallDataChanged(data);
            showRecallApproveModalChanged(true);
            return;
        }

        if (selectedBatch) {
            setIsBatchConfirmationOpen(true);
            return;
        }

        const result = await searchBatches({
            productCode: data.productCode.trim(),
            batchNumber: data.batchNumber?.trim() ?? '',
        }).catch(() => null);

        if (!result) return;

        setBatches(result);
        if (result.length === 0) {
            toast.error(t('pages.documents.batchDropout.noBatches'));
            return;
        }

        if (result.length === 1) {
            setSelectedBatch(result[0]);
            setIsBatchConfirmationOpen(true);
            return;
        }

        setIsBatchSelectionOpen(true);
    };

    const confirmBatchRecall = async () => {
        if (!selectedBatch) return;

        const data = getValues() as RecallData;
        const payload: Omit<BatchRecallParams, 'documentId'> = {
            reason: data.reason,
            productCode: data.productCode.trim(),
            batchNumber: selectedBatch.batchNumber || data.batchNumber?.trim() || '',
            batchId: selectedBatch.batchId,
            ...(data.documentNumber?.trim() && {
                regulatorDocumentNumber: data.documentNumber.trim(),
            }),
            ...(data.recallDate && {recallDecisionDate: formatISO(data.recallDate)}),
            ...(data.comment?.trim() && {comment: data.comment.trim()}),
        };

        try {
            if (await recallDocument.submit(payload, recallBatch))
                setIsBatchConfirmationOpen(false);
        } catch {
            // The effect watcher displays the server error.
        }
    };

    const batchOptions = useMemo(
        () =>
            batches.map((batch) => (
                <BatchSelectionItem
                    key={batch.batchId}
                    $selected={selectedBatch?.batchId === batch.batchId}
                >
                    <BatchSelectionRadio
                        type="radio"
                        name="adminDropoutBatch"
                        checked={selectedBatch?.batchId === batch.batchId}
                        onChange={() => setSelectedBatch(batch)}
                    />
                    <BatchSelectionDetails>
                        <BatchSelectionNumber>{batch.batchNumber || '-'}</BatchSelectionNumber>
                        <BatchSelectionMeta>
                            <span>
                                {t('pages.documents.batchDropout.productionDate')}:{' '}
                                {formatBatchDate(batch.productionDate)}
                            </span>
                            <span>
                                {t('pages.documents.batchDropout.expirationDate')}:{' '}
                                {formatBatchDate(batch.expirationDate)}
                            </span>
                            <span>
                                {t('pages.documents.batchDropout.organization')}:{' '}
                                {batch.organizationName || '-'}
                            </span>
                        </BatchSelectionMeta>
                    </BatchSelectionDetails>
                </BatchSelectionItem>
            )),
        [batches, selectedBatch, t],
    );

    return (
        <>
            <BackTo to={ROUTE.REGISTRIES_GTIN}>{t('pages.documents.back')}</BackTo>
            <RecallApproveModal />
            <ContentWrapper>
                <HeadlineText>{t('pages.products.recallForm.title')}</HeadlineText>
                <SubheadlineText>{t('pages.products.recallForm.description')}</SubheadlineText>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormWrapper>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Button
                                    disabled={isPending}
                                    style={{height: 'fit-content'}}
                                    variant={
                                        batchNumberSource === 'byCodeAndBatchNumber'
                                            ? ButtonVariant.Outline
                                            : ButtonVariant.OutlineSecondary
                                    }
                                    type="button"
                                    onClick={() => {
                                        setValue('batchNumberSource', 'byCode', {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        });
                                        setValue('reason', PRODUCT_RECALL_REASON, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        });
                                    }}
                                >
                                    <ButtonInner
                                        $active={batchNumberSource === 'byCodeAndBatchNumber'}
                                    >
                                        <span>{t('pages.products.recallForm.byProductCode')}</span>
                                        <span>
                                            {t('pages.products.recallForm.recallProductByGTIN')}
                                        </span>
                                    </ButtonInner>
                                </Button>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Button
                                    disabled={isPending}
                                    style={{height: 'fit-content'}}
                                    variant={
                                        batchNumberSource === 'byCode'
                                            ? ButtonVariant.Outline
                                            : ButtonVariant.OutlineSecondary
                                    }
                                    type="button"
                                    onClick={() => {
                                        setValue('batchNumberSource', 'byCodeAndBatchNumber', {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        });
                                        setValue('reason', '', {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        });
                                    }}
                                >
                                    <ButtonInner $active={batchNumberSource === 'byCode'}>
                                        <span>
                                            {t(
                                                'pages.products.recallForm.byProductCodeAndBatchNumber',
                                            )}
                                        </span>
                                        <span>{t('pages.products.recallForm.recallBatch')}</span>
                                    </ButtonInner>
                                </Button>
                            </Grid>
                        </Grid>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={12}>
                                <FormField>
                                    <Controller
                                        control={control}
                                        name="reason"
                                        render={(props) => (
                                            <Select
                                                isRequired
                                                disabled={isPending || !isBatchMode}
                                                onChange={(val: SelectOption<string>) => {
                                                    props.onChange(val.value);
                                                }}
                                                value={
                                                    reasons.filter(
                                                        (el: SelectOption<string>) =>
                                                            el.value === props.value,
                                                    )[0]
                                                }
                                                label={t(
                                                    'pages.products.recallForm.inputTitles.reason',
                                                )}
                                                hasError={!!formState.errors.reason}
                                                options={reasons}
                                            />
                                        )}
                                    />
                                </FormField>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="productCode"
                                    render={({onChange, value}) => (
                                        <Input
                                            disabled={isPending}
                                            isRequired
                                            label={t(
                                                'pages.products.recallForm.inputTitles.productCode',
                                            )}
                                            placeholder={t(
                                                'pages.products.recallForm.inputPlaceholders.productCode',
                                            )}
                                            value={value}
                                            onChange={onChange}
                                            hasError={!!formState.errors.productCode}
                                        />
                                    )}
                                />
                            </FormField>
                        </Grid>
                        <Grid
                            style={{
                                display:
                                    batchNumberSource === 'byCodeAndBatchNumber' ? 'block' : 'none',
                            }}
                            item
                            xs={12}
                            md={12}
                        >
                            <FormField>
                                <Controller
                                    control={control}
                                    name="batchNumber"
                                    render={({onChange, value}) => (
                                        <Input
                                            disabled={isPending}
                                            isRequired
                                            label={t(
                                                'pages.products.recallForm.inputTitles.batchNumber',
                                            )}
                                            placeholder={t(
                                                'pages.products.recallForm.inputPlaceholders.batchNumber',
                                            )}
                                            value={value}
                                            onChange={onChange}
                                            hasError={!!formState.errors.batchNumber}
                                        />
                                    )}
                                />
                                {formState.errors.batchNumber && (
                                    <span style={{fontSize: 12, color: 'red'}}>
                                        {t(formState.errors.batchNumber.message as string)}
                                    </span>
                                )}
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="documentNumber"
                                    render={({onChange, value}) => (
                                        <Input
                                            disabled={isPending}
                                            isRequired={batchNumberSource === 'byCode'}
                                            label={t(
                                                'pages.products.recallForm.inputTitles.regulatorDocumentNumber',
                                            )}
                                            placeholder={t(
                                                'pages.products.recallForm.inputPlaceholders.regulatorDocumentNumber',
                                            )}
                                            value={value}
                                            onChange={onChange}
                                            hasError={!!formState.errors.documentNumber}
                                        />
                                    )}
                                />
                            </FormField>
                        </Grid>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={12}>
                                <FormField>
                                    <DatePickerField>
                                        <Controller
                                            control={control}
                                            name="recallDate"
                                            render={({onChange, value}) => (
                                                <DatePicker
                                                    disabled={isPending}
                                                    placeholderText={t(
                                                        'pages.products.recallForm.inputPlaceholders.recallDecisionDate',
                                                    )}
                                                    isRequired
                                                    label={t(
                                                        'pages.products.recallForm.inputTitles.recallDecisionDate',
                                                    )}
                                                    maxDate={new Date()}
                                                    value={value}
                                                    onChange={(newDate: Date | null) => {
                                                        onChange(newDate);
                                                    }}
                                                    hasError={!!formState.errors.recallDate}
                                                />
                                            )}
                                        />
                                    </DatePickerField>
                                </FormField>
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <FormField>
                                    <Controller
                                        control={control}
                                        name="comment"
                                        render={({onChange, value}) => (
                                            <Input
                                                disabled={isPending}
                                                label={t(
                                                    'pages.products.recallForm.inputTitles.comment',
                                                )}
                                                placeholder={t(
                                                    'pages.products.recallForm.inputPlaceholders.comment',
                                                )}
                                                value={value}
                                                onChange={onChange}
                                                hasError={!!formState.errors.comment}
                                            />
                                        )}
                                    />
                                </FormField>
                            </Grid>
                        </Grid>
                        <NavigationButtonsContainer>
                            <div>
                                <Button
                                    style={{width: 'auto'}}
                                    variant={ButtonVariant.Outline}
                                    type="button"
                                    disabled={isPending}
                                    onClick={() => {
                                        history.push(ROUTE.REGISTRIES_GTIN);
                                    }}
                                >
                                    {t('pages.products.recallForm.cancel')}
                                </Button>
                            </div>
                            <div>
                                <Button
                                    isLoading={isBatchMode ? isBatchSearchPending : isRecallPending}
                                    style={{width: 'auto'}}
                                    variant={ButtonVariant.Normal}
                                    type="submit"
                                    disabled={!formState.isValid || isPending}
                                >
                                    {t('pages.products.recallForm.recall')}
                                </Button>
                            </div>
                        </NavigationButtonsContainer>
                    </FormWrapper>
                </form>
            </ContentWrapper>
            <Modal
                modalWidth={1}
                show={isBatchSelectionOpen}
                onClose={() => setIsBatchSelectionOpen(false)}
                title={t('pages.documents.batchDropout.selectionTitle')}
            >
                <BatchSelectionModalContent>
                    <BatchSelectionList>{batchOptions}</BatchSelectionList>
                    <BatchSelectionActions>
                        <Button
                            size="sm"
                            variant={ButtonVariant.Outline}
                            onClick={() => setIsBatchSelectionOpen(false)}
                        >
                            {t('pages.products.recallForm.recallConfirmationModal.cancel')}
                        </Button>
                        <Button
                            size="sm"
                            variant={ButtonVariant.Normal}
                            disabled={!selectedBatch}
                            onClick={() => {
                                setIsBatchSelectionOpen(false);
                                setIsBatchConfirmationOpen(true);
                            }}
                        >
                            {t('pages.documents.batchDropout.continue')}
                        </Button>
                    </BatchSelectionActions>
                </BatchSelectionModalContent>
            </Modal>
            <Modal
                modalWidth={0}
                show={isBatchConfirmationOpen}
                onClose={() => {
                    if (!isPending) setIsBatchConfirmationOpen(false);
                }}
                title={t('pages.documents.batchDropout.confirmationTitle')}
            >
                <Root>
                    <ModalText>
                        {t('pages.documents.batchDropout.confirmationDescription', {
                            batchNumber: selectedBatch?.batchNumber,
                        })}
                    </ModalText>
                    <ButtonWrapper>
                        <Button
                            size="sm"
                            variant={ButtonVariant.Outline}
                            disabled={isBatchRecallPending || recallDocument.pending}
                            onClick={() => setIsBatchConfirmationOpen(false)}
                        >
                            {t('pages.products.recallForm.recallConfirmationModal.cancel')}
                        </Button>
                        <Button
                            size="sm"
                            variant={ButtonVariant.Normal}
                            isLoading={isBatchRecallPending || recallDocument.pending}
                            onClick={confirmBatchRecall}
                        >
                            {t('pages.products.recallForm.recallConfirmationModal.recall')}
                        </Button>
                    </ButtonWrapper>
                </Root>
            </Modal>
        </>
    );
};
